// src/pages/etudiant/DashboardEtudiant.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { APP_CONFIG } from '../../config/appConfig';
import {
    BookOpen, CheckCircle, Loader2, AlertCircle, Play,
    Mail, Phone, XCircle, RefreshCw,
    Key, Lock, UserCheck, Building2, TrendingUp, UserCircle,
    Clock, ClipboardList, GraduationCap, Calendar,
    ChevronLeft, ChevronRight
} from 'lucide-react';
import LecteurFormation from '../../components/communs/LecteurFormation.jsx';
import LecteurEvaluation from '../../components/communs/LecteurEvaluation.jsx';
import LecteurEvaluationRich from '../../components/communs/LecteurEvaluationRich.jsx';

// ==========================================================================
// Composants de présentation réutilisables (inchangés)
// ==========================================================================

const Badge = ({ children, className = '' }) => (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] sm:text-xs font-semibold ${className}`}>
        {children}
    </span>
);

const StatTile = ({ icon: Icon, label, value, accent }) => (
    <div className="flex items-center gap-2 sm:gap-3 rounded-lg bg-gray-50 border border-gray-100 px-3 sm:px-4 py-2 sm:py-3 min-w-[120px] sm:min-w-[160px]">
        <div className={`flex items-center justify-center w-7 h-7 sm:w-9 sm:h-9 rounded-lg shrink-0 ${accent}`}>
            {Icon && <Icon className="w-3.5 h-3.5 sm:w-[18px] sm:h-[18px] text-white" />}
        </div>
        <div className="min-w-0">
            <div className="text-base sm:text-lg font-semibold text-gray-900 leading-none">{value}</div>
            <div className="text-[10px] sm:text-xs text-gray-500 mt-1 truncate">{label}</div>
        </div>
    </div>
);

const ActionButton = ({ icon: Icon, children, onClick, disabled, variant = 'primary', size = 'sm' }) => {
    const variants = {
        primary: 'bg-blue-600 hover:bg-blue-700 text-white',
        success: 'bg-emerald-600 hover:bg-emerald-700 text-white',
        secondary: 'bg-gray-600 hover:bg-gray-700 text-white',
        danger: 'bg-red-50 hover:bg-red-100 text-red-700 border border-red-200'
    };
    const sizeClasses = size === 'sm' ? 'px-2 py-1 text-[10px] sm:px-3 sm:py-1.5 sm:text-xs' : 'px-3 py-1.5 text-xs';
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            className={`inline-flex items-center gap-1 sm:gap-1.5 ${sizeClasses} rounded-lg font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed ${variants[variant]}`}
        >
            {Icon && <Icon className="w-2.5 h-2.5 sm:w-3 sm:h-3" />}
            {children}
        </button>
    );
};

// ==========================================================================
// Modal de changement de mot de passe (inchangée)
// ==========================================================================

const PasswordChangeModal = ({ isOpen, onClose, onSuccess }) => {
    const { token } = useAuth();
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const resetFields = () => {
        setOldPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setError('');
    };

    const handleClose = () => {
        resetFields();
        onClose();
    };

    const validatePassword = (password) => {
        if (password.length < 6) return 'Le mot de passe doit contenir au moins 6 caractères.';
        if (!/[A-Z]/.test(password)) return 'Le mot de passe doit contenir au moins une majuscule.';
        if (!/\d/.test(password)) return 'Le mot de passe doit contenir au moins un chiffre.';
        if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) {
            return 'Le mot de passe doit contenir au moins un caractère spécial.';
        }
        return null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (newPassword !== confirmPassword) {
            setError('Les mots de passe ne correspondent pas');
            return;
        }

        const validationError = validatePassword(newPassword);
        if (validationError) {
            setError(validationError);
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/auth/change-password`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ oldPassword, newPassword }),
            });

            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                throw new Error(data.message || data.error || 'Erreur lors du changement de mot de passe');
            }

            alert('✅ Mot de passe modifié avec succès');
            resetFields();
            if (onSuccess) onSuccess();
            onClose();
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
            <div className="bg-white rounded-xl shadow-2xl p-4 sm:p-6 w-full max-w-md">
                <div className="flex items-center justify-between mb-4 sm:mb-5">
                    <h3 className="text-sm sm:text-base font-semibold text-gray-900 flex items-center gap-2">
                        <Key className="w-4 h-4 sm:w-[18px] sm:h-[18px] text-blue-600" />
                        Changer le mot de passe
                    </h3>
                    <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 text-sm">
                        ✕
                    </button>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-[10px] sm:text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                            Mot de passe actuel
                        </label>
                        <input
                            type="password"
                            autoComplete="current-password"
                            value={oldPassword}
                            onChange={(e) => setOldPassword(e.target.value)}
                            className="w-full px-3 py-2 text-xs sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-[10px] sm:text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                            Nouveau mot de passe
                        </label>
                        <input
                            type="password"
                            autoComplete="new-password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            className="w-full px-3 py-2 text-xs sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            required
                        />
                        <p className="text-[10px] sm:text-xs text-gray-400 mt-1">
                            Min. 6 caractères, une majuscule, un chiffre, un caractère spécial
                        </p>
                    </div>
                    <div>
                        <label className="block text-[10px] sm:text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                            Confirmer le mot de passe
                        </label>
                        <input
                            type="password"
                            autoComplete="new-password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            className="w-full px-3 py-2 text-xs sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            required
                        />
                    </div>
                    {error && <p className="text-xs sm:text-sm text-red-600">{error}</p>}
                    <div className="flex justify-end gap-3 pt-2">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-gray-600 hover:text-gray-800"
                        >
                            Annuler
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-4 sm:px-5 py-1.5 sm:py-2 text-xs sm:text-sm font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
                        >
                            {loading ? 'Modification...' : 'Confirmer'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// ==========================================================================
// Composant principal DashboardEtudiant (version paginée)
// ==========================================================================

export default function DashboardEtudiant() {
    const { token, user, isAuthenticated } = useAuth();
    const etudiantId = user?.id;

    // États pour la pagination
    const PAGE_SIZE = 5;
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);

    // États existants
    const [items, setItems] = useState([]);
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [showFormation, setShowFormation] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    // eslint-disable-next-line no-unused-vars
    const [refreshKey, setRefreshKey] = useState(0);

    // Fonction pour obtenir le libellé du type
    const getTypeLabel = (type) => {
        if (type === 'FORMATION') return 'Formation';
        if (type === 'EVALUATION') return 'Évaluation';
        if (type === 'EVALUATION_RICH') return 'Évaluation enrichie';
        return 'Inconnu';
    };

    // Chargement des données avec pagination
    const loadData = useCallback(async ({ page = 0, silent = false } = {}) => {
        if (!isAuthenticated || !token || !etudiantId) {
            setLoading(false);
            return;
        }

        if (!silent) setLoading(true);
        setError(null);
        try {
            const response = await fetch(
                `${APP_CONFIG.BASE_URL}/api/v1/etudiant/dashboard?etudiantId=${etudiantId}&page=${page}&size=${PAGE_SIZE}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error(`Erreur HTTP ${response.status}`);

            const data = await response.json();

            // Nouvelle structure : DashboardResponseDTO
            // Contient : etudiant, client, totalFormations, totalEvaluations, progressionGlobale, inscriptions (Page)
            const content = data.inscriptions?.content || [];
            const totalPagesFromServer = data.inscriptions?.totalPages ?? 0;
            const totalElementsFromServer = data.inscriptions?.totalElements ?? content.length;

            setTotalPages(totalPagesFromServer);
            setTotalElements(totalElementsFromServer);

            // Mapper les items
            const unifiedItems = content.map(ins => ({
                id: ins.id,
                type: ins.type,
                refId: ins.refId,
                code: ins.code || '—',
                titre: ins.titre || (ins.type === 'FORMATION' ? 'Formation sans titre' : 'Évaluation sans titre'),
                dateDebut: ins.dateDebut || null,
                dateFin: ins.dateFin || null,
                statut: ins.statut,
                codeEtudiant: ins.codeEtudiant || user?.codeEtudiant || '—',
                progression: ins.progression ?? 0,
                formationId: ins.type === 'FORMATION' ? ins.refId : null,
                horsDelais: ins.horsDelais || false,
                _original: ins
            }));

            setItems(unifiedItems);
            setCurrentPage(page);

            // Mettre à jour les informations d'en-tête (étudiant, client, totaux)
            setDashboardData({
                etudiant: data.etudiant || user,
                client: data.client || null,
                totalFormations: data.totalFormations ?? 0,
                totalEvaluations: data.totalEvaluations ?? 0,
                progressionGlobale: data.progressionGlobale ?? 0
            });
        } catch (err) {
            console.error('Erreur chargement dashboard :', err);
            setError(err.message);
        } finally {
            if (!silent) setLoading(false);
        }
    }, [isAuthenticated, token, etudiantId, PAGE_SIZE, user]);

    // Chargement initial : uniquement loadData (plus de loadStats)
    useEffect(() => {
        loadData({ page: 0 });
    }, [loadData, refreshKey]);

    // Recharger la page courante après une action (ex: annulation)
    const refreshCurrentPage = useCallback(() => {
        loadData({ page: currentPage, silent: true });
    }, [loadData, currentPage]);

    // Utilitaires pour les statuts
    const getStatusBadge = (statut) => {
        const map = {
            'EN_ATTENTE': 'bg-amber-100 text-amber-700',
            'VALIDEE': 'bg-blue-100 text-blue-700',
            'INSCRIT': 'bg-green-100 text-green-700',
            'EN_COURS': 'bg-indigo-100 text-indigo-700',
            'TERMINE': 'bg-gray-100 text-gray-700',
            'REJETEE': 'bg-red-100 text-red-700',
            'ANNULEE': 'bg-red-100 text-red-700',
            'ABANDONNEE': 'bg-gray-100 text-gray-700'
        };
        return map[statut] || 'bg-gray-100 text-gray-700';
    };

    const getStatusLabel = (statut) => {
        const map = {
            'EN_ATTENTE': 'En attente',
            'VALIDEE': 'Validée',
            'INSCRIT': 'Inscrit',
            'EN_COURS': 'En cours',
            'TERMINE': 'Terminé',
            'REJETEE': 'Rejetée',
            'ANNULEE': 'Annulée',
            'ABANDONNEE': 'Abandonnée'
        };
        return map[statut] || statut;
    };

    // ==================== ACTIONS ====================
    const handleAnnuler = async (item) => {
        if (!window.confirm('Voulez-vous vraiment annuler cette inscription ?')) return;
        setActionLoading(true);
        try {
            const id = item.id;
            const response = await fetch(
                `${APP_CONFIG.BASE_URL}/api/v1/etudiant/inscriptions/${id}/annuler?etudiantId=${etudiantId}`,
                { method: 'PATCH', headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error('Erreur lors de l\'annulation');
            alert('✅ Inscription annulée');
            refreshCurrentPage();
        } catch (err) {
            alert('❌ ' + err.message);
            setError(err.message);
        } finally {
            setActionLoading(false);
        }
    };

    const handleAbandonner = async (item) => {
        if (!window.confirm('Voulez-vous vraiment abandonner cette formation/évaluation en cours ?')) return;
        setActionLoading(true);
        try {
            const id = item.id;
            const response = await fetch(
                `${APP_CONFIG.BASE_URL}/api/v1/etudiant/inscriptions/${id}/abandonner?etudiantId=${etudiantId}`,
                { method: 'PATCH', headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error('Erreur lors de l\'abandon');
            alert('✅ Inscription abandonnée');
            refreshCurrentPage();
        } catch (err) {
            alert('❌ ' + err.message);
            setError(err.message);
        } finally {
            setActionLoading(false);
        }
    };

    const handleDemarrer = async (item) => {
        setActionLoading(true);
        try {
            if (item.type === 'FORMATION') {
                const formationId = item.refId;
                if (!formationId) throw new Error('ID de formation manquant');
                const response = await fetch(
                    `${APP_CONFIG.BASE_URL}/api/v1/etudiant/formations/${formationId}/demarrer?etudiantId=${etudiantId}`,
                    { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } }
                );
                if (!response.ok) throw new Error('Impossible de démarrer la formation');
                refreshCurrentPage();
                handleAcceder(item);
            } else {
                handleAcceder(item);
            }
        } catch (err) {
            alert('❌ ' + err.message);
            setError(err.message);
        } finally {
            setActionLoading(false);
        }
    };

    const handleActionDemarrer = (item) => handleDemarrer(item);

    const handleAcceder = (item) => {
        if (!item || !item.refId) {
            console.warn("Item invalide pour l'accès (refId manquant)", item);
            alert("Impossible d'accéder à cet élément : données manquantes. Merci de rafraîchir la page.");
            return;
        }
        setSelectedItem(item);
        setShowFormation(true);
    };

    const handleTerminerFormation = async (inscriptionId) => {
        try {
            const response = await fetch(
                `${APP_CONFIG.BASE_URL}/api/v1/etudiant/inscriptions/${inscriptionId}/terminer?etudiantId=${etudiantId}`,
                { method: 'PATCH', headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error('Erreur lors de la terminaison de la formation');
            alert('✅ Formation terminée avec succès !');
            refreshCurrentPage();
        } catch (err) {
            alert('❌ ' + err.message);
            setError(err.message);
        }
    };

    const handleDemanderReprise = async (inscriptionId) => {
        if (!window.confirm('Voulez-vous demander la reprise de cette formation/évaluation auprès du client ?')) return;
        setActionLoading(true);
        try {
            const response = await fetch(
                `${APP_CONFIG.BASE_URL}/api/v1/etudiant/inscriptions/${inscriptionId}/demander-reprise?etudiantId=${etudiantId}`,
                { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error('Erreur lors de la demande de reprise');
            alert('✅ Demande de reprise envoyée au client.');
            refreshCurrentPage();
        } catch (err) {
            alert('❌ ' + err.message);
            setError(err.message);
        } finally {
            setActionLoading(false);
        }
    };

    const handleCloseLecteur = (data) => {
        setShowFormation(false);
        setSelectedItem(null);
        if (data && data.formationCompleted && selectedItem) {
            handleTerminerFormation(selectedItem.id);
        }
        // On recharge la page courante après fermeture
        refreshCurrentPage();
    };

    const handlePasswordChangeSuccess = () => {
        alert('Mot de passe mis à jour');
        refreshCurrentPage();
    };

    // ==================== MESSAGE DYNAMIQUE AMÉLIORÉ ====================
    const getDynamicMessage = () => {
        if (items.length === 0) {
            return "Bienvenue sur votre espace de formation. Consultez vos formations et évaluations ci-dessous.";
        }
        const priorities = ['EN_COURS', 'INSCRIT', 'EN_ATTENTE', 'VALIDEE', 'TERMINE'];
        let bestItem = items[0];
        let bestPriority = -1;
        for (const item of items) {
            const statut = item.statut;
            const idx = priorities.indexOf(statut);
            if (idx !== -1 && (bestPriority === -1 || idx < bestPriority)) {
                bestPriority = idx;
                bestItem = item;
            }
        }
        if (bestPriority === -1) {
            for (const item of items) {
                if (['REJETEE', 'ANNULEE', 'ABANDONNEE'].includes(item.statut)) {
                    bestItem = item;
                    break;
                }
            }
        }
        const statut = bestItem.statut;
        const horsDelais = bestItem.horsDelais;
        if (horsDelais) {
            return "⚠️ Votre inscription est hors délais. Veuillez contacter votre client pour régulariser votre situation.";
        }
        switch (statut) {
            case 'EN_ATTENTE': return "📌 Votre inscription est en attente de validation par le fournisseur. Vous pouvez l'annuler si nécessaire.";
            case 'VALIDEE': return "✅ Votre dossier est validé. Vous pouvez désormais démarrer votre formation lorsque vous êtes prêt.";
            case 'INSCRIT': return "📝 Vos droits sont OK. Vous êtes inscrit(e). Cliquez sur 'Démarrer' pour commencer votre formation.";
            case 'EN_COURS': return "🔄 Formation ou évaluation en cours. Continuez là où vous vous êtes arrêté.";
            case 'TERMINE': return "🎉 Félicitations ! Vous avez terminé cette formation/évaluation. Vous pouvez la revoir à tout moment.";
            case 'REJETEE':
            case 'ANNULEE':
            case 'ABANDONNEE': return "ℹ️ Cette inscription est dans un état final (rejetée, annulée ou abandonnée). Aucune action n'est disponible.";
            default: return "Bienvenue sur votre espace de formation. Consultez vos formations et évaluations ci-dessous.";
        }
    };

    // ==================== RENDU ====================
    if (!isAuthenticated) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <div className="text-center">
                    <AlertCircle className="w-10 h-10 sm:w-12 sm:h-12 text-amber-500 mx-auto mb-4" />
                    <p className="text-xs sm:text-sm text-gray-600">Veuillez vous connecter.</p>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <Loader2 className="animate-spin text-blue-600 w-10 h-10 sm:w-12 sm:h-12" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="max-w-4xl mx-auto p-4 sm:p-6">
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 sm:p-6 text-center">
                    <AlertCircle className="w-10 h-10 sm:w-12 sm:h-12 text-red-500 mx-auto mb-4" />
                    <p className="text-xs sm:text-sm text-red-600 font-medium">{error}</p>
                    <button onClick={() => loadData({ page: 0 })} className="mt-4 px-3 sm:px-4 py-1.5 sm:py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition text-xs sm:text-sm">
                        Réessayer
                    </button>
                </div>
            </div>
        );
    }

    // ⭐ Rendu conditionnel des lecteurs (découplage total)
    if (showFormation && selectedItem) {
        if (selectedItem.type === 'FORMATION') {
            return (
                <div className="max-w-6xl mx-auto p-3 sm:p-4">
                    <LecteurFormation
                        formationId={selectedItem.refId}
                        etudiantId={etudiantId}
                        onClose={handleCloseLecteur}
                        onComplete={handleCloseLecteur}
                        onProgress={() => {}}
                    />
                </div>
            );
        } else if (selectedItem.type === 'EVALUATION') {
            return (
                <div className="max-w-6xl mx-auto p-3 sm:p-4">
                    <LecteurEvaluation
                        evaluationId={selectedItem.refId}
                        etudiantId={etudiantId}
                        onClose={handleCloseLecteur}
                        onSubmitSuccess={refreshCurrentPage}
                    />
                </div>
            );
        } else if (selectedItem.type === 'EVALUATION_RICH') {
            return (
                <div className="max-w-6xl mx-auto p-3 sm:p-4">
                    <LecteurEvaluationRich
                        evaluationId={selectedItem.refId}
                        etudiantId={etudiantId}
                        onClose={handleCloseLecteur}
                        onSubmitSuccess={refreshCurrentPage}
                    />
                </div>
            );
        } else {
            // Type non reconnu
            return (
                <div className="max-w-4xl mx-auto p-4 text-center">
                    <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                    <p className="text-sm text-gray-600">Type d'inscription non pris en charge.</p>
                    <button onClick={() => setShowFormation(false)} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg">
                        Retour
                    </button>
                </div>
            );
        }
    }

    const etudiant = dashboardData?.etudiant || {};
    const client = dashboardData?.client || {};
    const totalFormations = dashboardData?.totalFormations ?? 0;
    const totalEvaluations = dashboardData?.totalEvaluations ?? 0;
    const progressionGlobale = dashboardData?.progressionGlobale ?? 0;

    const dynamicMessage = getDynamicMessage();

    return (
        <div className="max-w-6xl mx-auto p-3 sm:p-4 md:p-6 space-y-4 sm:space-y-6 text-gray-800">
            {/* Cadre 1 : Mes informations (inchangé) */}
            <section className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-100 bg-gray-50/60">
                    <div className="flex items-center gap-2">
                        <UserCircle className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-700" />
                        <h2 className="text-sm sm:text-base font-semibold text-gray-900 tracking-tight">Mes informations</h2>
                    </div>
                    <button
                        onClick={() => setShowPasswordModal(true)}
                        title="Changer le mot de passe"
                        className="inline-flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 transition text-[10px] sm:text-xs font-medium text-gray-600"
                    >
                        <Lock className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-gray-500" />
                        Mot de passe
                    </button>
                </div>
                <div className="p-4 sm:p-6 bg-orange-300">
                    <div className="flex flex-col md:flex-row items-center md:items-start gap-4 sm:gap-6">
                        <div className="shrink-0">
                            <div className="w-40 h-20 sm:w-48 sm:h-28 rounded-xl bg-emerald-800 flex items-center justify-center shadow-sm">
                                <div className="w-32 h-16 sm:w-40 sm:h-20 bg-white rounded-lg p-2 flex items-center justify-center shadow-inner">
                                    <img
                                        src="/images/logoiss.png"
                                        alt="ISS Logo"
                                        className="max-w-full max-h-full object-contain"
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                            const parent = e.target.parentNode;
                                            parent.innerHTML = `
                                                <div class="text-[#009640] text-xl sm:text-2xl font-black leading-none">ISS</div>
                                                <div class="text-[#009640]/60 text-[6px] sm:text-[7px] mt-0.5 uppercase tracking-widest text-center">Ivoire Soft Services</div>
                                            `;
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="flex-1 w-full text-center md:text-left">
                            <div className="flex flex-col md:flex-row md:items-center gap-2 justify-center md:justify-start">
                                <h3 className="text-lg sm:text-xl font-semibold text-gray-900">
                                    {etudiant?.prenom || ''} {etudiant?.nom || ''}
                                </h3>
                                <Badge className={etudiant?.actif ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}>
                                    {etudiant?.actif ? 'Actif' : 'Inactif'}
                                </Badge>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 mt-3 text-xs sm:text-sm text-gray-600">
                                <div className="flex items-center justify-center md:justify-start gap-2">
                                    <Mail className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-gray-400 shrink-0" />
                                    <span>{etudiant?.email || 'Non renseigné'}</span>
                                </div>
                                <div className="flex items-center justify-center md:justify-start gap-2">
                                    <Phone className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-gray-400 shrink-0" />
                                    <span>{etudiant?.mobile || 'Non renseigné'}</span>
                                </div>
                            </div>
                            {client && (
                                <div className="mt-3 pt-3 border-t border-gray-100">
                                    <div className="text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1.5 text-center md:text-left">
                                        Organisme
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-1.5 text-xs sm:text-sm text-gray-600">
                                        <div className="flex items-center justify-center md:justify-start gap-2">
                                            <Building2 className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-gray-400 shrink-0" />
                                            <span>Code : <strong className="text-gray-800">{client.code || 'N/A'}</strong></span>
                                        </div>
                                        <div className="flex items-center justify-center md:justify-start gap-2">
                                            <Mail className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-gray-400 shrink-0" />
                                            <span>{client.email || 'N/A'}</span>
                                        </div>
                                        <div className="flex items-center justify-center md:justify-start gap-2">
                                            <Phone className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-gray-400 shrink-0" />
                                            <span>{client.mobile || 'N/A'}</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-100">
                        <StatTile icon={BookOpen} label="Formations" value={totalFormations} accent="bg-blue-600" />
                        <StatTile icon={ClipboardList} label="Évaluations" value={totalEvaluations} accent="bg-purple-600" />
                        <StatTile icon={TrendingUp} label="Progression globale" value={`${progressionGlobale}%`} accent="bg-emerald-600" />
                    </div>
                </div>
            </section>

            {/* Cadre 2 : Mes inscriptions (version paginée) */}
            <section className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-100 bg-gray-50/60">
                    <div className="flex items-center gap-2">
                        <GraduationCap className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-700" />
                        <h2 className="text-sm sm:text-base font-semibold text-gray-900 tracking-tight">Mes inscriptions</h2>
                        <Badge className="bg-gray-100 text-gray-600">
                            {totalElements} élément{totalElements > 1 ? 's' : ''}
                        </Badge>
                        {actionLoading && (
                            <Loader2 className="w-3 h-3 sm:w-3.5 sm:h-3.5 animate-spin text-gray-400" />
                        )}
                    </div>
                    <span className="text-[10px] sm:text-xs text-gray-400">
                        Page {currentPage + 1} / {totalPages || 1}
                    </span>
                </div>

                {items.length === 0 ? (
                    <div className="p-8 sm:p-12 text-center">
                        <BookOpen className="w-10 h-10 sm:w-12 sm:h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-xs sm:text-sm text-gray-600 font-medium">Aucune formation ou évaluation</p>
                        <p className="text-[10px] sm:text-xs text-gray-400 mt-1">Vous n'êtes inscrit à aucune formation ou évaluation pour le moment.</p>
                    </div>
                ) : (
                    <>
                        {/* Version mobile : cartes (inchangé) */}
                        <div className="sm:hidden space-y-3 p-3">
                            {items.map((item) => {
                                const statutEtudiant = item.statut;
                                const isCompleted = statutEtudiant === 'TERMINE';
                                const isInProgress = statutEtudiant === 'EN_COURS';
                                const isInscrit = statutEtudiant === 'INSCRIT';
                                const isEnAttente = statutEtudiant === 'EN_ATTENTE';
                                const isValidee = statutEtudiant === 'VALIDEE';
                                const isFinal = ['REJETEE', 'ANNULEE', 'ABANDONNEE'].includes(statutEtudiant);

                                const formatDate = (date) => {
                                    if (!date) return '—';
                                    return new Date(date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
                                };
                                const dateDebutAffichee = formatDate(item.dateDebut);
                                const dateFinAffichee = formatDate(item.dateFin);

                                let typeBadgeClass = 'bg-gray-100 text-gray-700';
                                if (item.type === 'FORMATION') typeBadgeClass = 'bg-blue-50 text-blue-700';
                                else if (item.type === 'EVALUATION') typeBadgeClass = 'bg-purple-50 text-purple-700';
                                else if (item.type === 'EVALUATION_RICH') typeBadgeClass = 'bg-indigo-50 text-indigo-700';

                                return (
                                    <div key={item.id} className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
                                        <div className="flex justify-between items-start">
                                            <div className="flex items-center gap-1.5">
                                                <UserCheck className="w-3 h-3 text-gray-400" />
                                                <span className="text-xs font-medium text-gray-700">{item.codeEtudiant}</span>
                                            </div>
                                            <Badge className={typeBadgeClass}>
                                                {getTypeLabel(item.type)}
                                            </Badge>
                                        </div>
                                        <div className="mt-1 font-mono text-xs font-bold text-gray-900 tracking-wide">{item.code} {item.titre}</div>
                                        <div className="mt-1 text-xs text-gray-600 flex flex-col gap-0.5">
                                            <span className="inline-flex items-center gap-1">
                                                <Calendar className="w-2.5 h-2.5 text-gray-400" />
                                                Début : {dateDebutAffichee}
                                            </span>
                                            <span className="inline-flex items-center gap-1">
                                                <Calendar className="w-2.5 h-2.5 text-gray-400" />
                                                Fin : {dateFinAffichee}
                                            </span>
                                        </div>
                                        <div className="mt-2 flex flex-wrap items-center gap-1.5">
                                            <Badge className={getStatusBadge(statutEtudiant)}>
                                                {getStatusLabel(statutEtudiant)}
                                            </Badge>
                                            {item.horsDelais && (
                                                <Badge className="bg-red-100 text-red-700">
                                                    ⚠️ Hors délais
                                                </Badge>
                                            )}
                                            <span className="text-[10px] text-gray-500 ml-auto">Progression : {item.progression}%</span>
                                        </div>
                                        <div className="mt-3 flex flex-wrap gap-2">
                                            {isInscrit && (
                                                <ActionButton icon={Play} variant="primary" onClick={() => handleActionDemarrer(item)} disabled={actionLoading} size="sm">
                                                    Démarrer
                                                </ActionButton>
                                            )}
                                            {isInProgress && (
                                                <ActionButton icon={RefreshCw} variant="success" onClick={() => handleAcceder(item)} disabled={actionLoading} size="sm">
                                                    Continuer
                                                </ActionButton>
                                            )}
                                            {isCompleted && (
                                                <ActionButton icon={RefreshCw} variant="secondary" onClick={() => handleDemanderReprise(item.id)} disabled={actionLoading} size="sm">
                                                    Reprise
                                                </ActionButton>
                                            )}
                                            {(isEnAttente || isValidee) && (
                                                <ActionButton icon={XCircle} variant="danger" onClick={() => handleAnnuler(item)} disabled={actionLoading} size="sm">
                                                    Annuler
                                                </ActionButton>
                                            )}
                                            {isInProgress && (
                                                <ActionButton icon={XCircle} variant="danger" onClick={() => handleAbandonner(item)} disabled={actionLoading} size="sm">
                                                    Abandonner
                                                </ActionButton>
                                            )}
                                            {isFinal && (
                                                <span className="text-xs text-gray-400">—</span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Version desktop : tableau (inchangé) */}
                        <div className="hidden sm:block overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="bg-gray-50 text-gray-900 text-[10px] sm:text-xs font-semibold uppercase tracking-wider">
                                        <th className="px-4 sm:px-6 py-3 text-left">Inscrit</th>
                                        <th className="px-3 sm:px-4 py-3 text-left">Type</th>
                                        <th className="px-3 sm:px-4 py-3 text-left">Code / Description</th>
                                        <th className="px-3 sm:px-4 py-3 text-left">Période</th>
                                        <th className="px-3 sm:px-4 py-3 text-left">Statut</th>
                                        <th className="px-3 sm:px-4 py-3 text-center">Progression</th>
                                        <th className="px-3 sm:px-4 py-3 text-center">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {items.map((item) => {
                                        const statutEtudiant = item.statut;
                                        const isCompleted = statutEtudiant === 'TERMINE';
                                        const isInProgress = statutEtudiant === 'EN_COURS';
                                        const isInscrit = statutEtudiant === 'INSCRIT';
                                        const isEnAttente = statutEtudiant === 'EN_ATTENTE';
                                        const isValidee = statutEtudiant === 'VALIDEE';
                                        const isFinal = ['REJETEE', 'ANNULEE', 'ABANDONNEE'].includes(statutEtudiant);

                                        const formatDate = (date) => {
                                            if (!date) return '—';
                                            return new Date(date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
                                        };
                                        const dateDebutAffichee = formatDate(item.dateDebut);
                                        const dateFinAffichee = formatDate(item.dateFin);

                                        let typeBadgeClass = 'bg-gray-100 text-gray-700';
                                        if (item.type === 'FORMATION') typeBadgeClass = 'bg-blue-50 text-blue-700';
                                        else if (item.type === 'EVALUATION') typeBadgeClass = 'bg-purple-50 text-purple-700';
                                        else if (item.type === 'EVALUATION_RICH') typeBadgeClass = 'bg-indigo-50 text-indigo-700';

                                        return (
                                            <tr key={item.id} className="hover:bg-gray-50/70 transition-colors">
                                                <td className="px-4 sm:px-6 py-3">
                                                    <span className="inline-flex items-center gap-1.5 text-gray-700 font-medium text-xs sm:text-sm">
                                                        <UserCheck className="w-3 h-3 text-gray-400" />
                                                        {item.codeEtudiant}
                                                    </span>
                                                </td>
                                                <td className="px-3 sm:px-4 py-3 uppercase">
                                                    <Badge className={typeBadgeClass}>
                                                        {getTypeLabel(item.type)}
                                                    </Badge>
                                                </td>
                                                <td className="px-3 sm:px-4 py-3">
                                                    <div className="font-mono text-xs font-bold text-gray-900 tracking-wide">{item.code} {item.titre}</div>
                                                </td>
                                                <td className="px-3 sm:px-4 py-3">
                                                    <div className="flex flex-col gap-0.5">
                                                        <span className="inline-flex items-center gap-1.5 text-gray-600 text-xs">
                                                            <Calendar className="w-3 h-3 text-gray-400" />
                                                            Début : {dateDebutAffichee}
                                                        </span>
                                                        <span className="inline-flex items-center gap-1.5 text-gray-600 text-xs">
                                                            <Calendar className="w-3 h-3 text-gray-400" />
                                                            Fin : {dateFinAffichee}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-3 sm:px-4 py-3">
                                                    <div className="flex flex-wrap gap-1">
                                                        <Badge className={getStatusBadge(statutEtudiant)}>
                                                            {getStatusLabel(statutEtudiant)}
                                                        </Badge>
                                                        {item.horsDelais && (
                                                            <Badge className="bg-red-100 text-red-700">
                                                                ⚠️ Hors délais
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-3 sm:px-4 py-3 text-center">
                                                    {item.progression}%
                                                    {isInscrit && (
                                                        <ActionButton icon={Play} variant="primary" onClick={() => handleActionDemarrer(item)} disabled={actionLoading} size="sm">
                                                            Démarrer
                                                        </ActionButton>
                                                    )}
                                                    {isInProgress && (
                                                        <ActionButton icon={RefreshCw} variant="success" onClick={() => handleAcceder(item)} disabled={actionLoading} size="sm">
                                                            Continuer
                                                        </ActionButton>
                                                    )}
                                                    {isCompleted && (
                                                        <ActionButton icon={RefreshCw} variant="secondary" onClick={() => handleDemanderReprise(item.id)} disabled={actionLoading} size="sm">
                                                            Reprise
                                                        </ActionButton>
                                                    )}
                                                    {(isEnAttente || isValidee || isFinal) && (
                                                        <span className="text-gray-300">—</span>
                                                    )}
                                                </td>
                                                <td className="px-3 sm:px-4 py-3 text-center">
                                                    {(isEnAttente || isValidee) && (
                                                        <ActionButton icon={XCircle} variant="danger" onClick={() => handleAnnuler(item)} disabled={actionLoading} size="sm">
                                                            Annuler
                                                        </ActionButton>
                                                    )}
                                                    {isInProgress && (
                                                        <ActionButton icon={XCircle} variant="danger" onClick={() => handleAbandonner(item)} disabled={actionLoading} size="sm">
                                                            Abandonner
                                                        </ActionButton>
                                                    )}
                                                    {(isInscrit || isFinal || isCompleted) && (
                                                        <span className="text-gray-300">—</span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-t border-gray-100 bg-gray-50/60 text-xs sm:text-sm text-gray-600">
                            <span>
                                Affichage {items.length} inscription{items.length > 1 ? 's' : ''}
                                {totalElements > 0 && ` (sur ${totalElements})`}
                            </span>
                            <div className="flex items-center gap-1 sm:gap-2">
                                <button
                                    onClick={() => loadData({ page: currentPage - 1 })}
                                    disabled={currentPage === 0 || loading}
                                    className="p-1.5 sm:p-2 rounded-lg hover:bg-gray-200 disabled:opacity-30 transition flex items-center gap-1"
                                >
                                    <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                                    <span className="hidden sm:inline">Précédent</span>
                                </button>
                                <span className="px-2 text-xs sm:text-sm font-medium">
                                    Page {currentPage + 1} / {Math.max(1, totalPages)}
                                </span>
                                <button
                                    onClick={() => loadData({ page: currentPage + 1 })}
                                    disabled={currentPage >= totalPages - 1 || loading}
                                    className="p-1.5 sm:p-2 rounded-lg hover:bg-gray-200 disabled:opacity-30 transition flex items-center gap-1"
                                >
                                    <span className="hidden sm:inline">Suivant</span>
                                    <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </section>

            {/* Message dynamique */}
            <div className="flex items-start gap-2 sm:gap-3 bg-blue-50/70 border border-blue-100 rounded-xl px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-blue-900">
                <span className="text-sm sm:text-base leading-none mt-0.5">💡</span>
                <span>{dynamicMessage}</span>
            </div>

            {/* Modal de changement de mot de passe */}
            <PasswordChangeModal
                isOpen={showPasswordModal}
                onClose={() => setShowPasswordModal(false)}
                onSuccess={handlePasswordChangeSuccess}
            />
        </div>
    );
}